import React, { useMemo, useState } from 'react';
import { ET_TIMEZONE } from '../utils/marketUtils';

// Helper function to calculate pairs from records using FIFO
const calculatePairsFromRecords = (records) => {
  const entryPrices = {};
  const exitPrices = {};

  records.forEach(record => {
    const symbol = record.symbol;
    if (record.action === 'entry') {
      if (!entryPrices[symbol]) entryPrices[symbol] = [];
      entryPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp,
        dateOnly: record.dateOnly
      });
    } else if (record.action === 'exit') {
      if (!exitPrices[symbol]) exitPrices[symbol] = [];
      exitPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp,
        dateOnly: record.dateOnly
      });
    }
  });

  const pairs = [];
  Object.keys(entryPrices).forEach(symbol => {
    const entries = [...entryPrices[symbol]];
    const exits = exitPrices[symbol] || [];

    exits.forEach(exit => {
      if (entries.length > 0) {
        const entry = entries.shift();
        const qty = Math.min(entry.quantity, exit.quantity);
        pairs.push({
          symbol,
          profit: (exit.price - entry.price) * qty,
          quantity: qty,
          entry: {
            uiRecord: {
              actualPrice: entry.price,
              timestamp: entry.timestamp,
              dateOnly: entry.dateOnly,
              quantity: qty
            }
          },
          exit: {
            uiRecord: {
              actualPrice: exit.price,
              timestamp: exit.timestamp,
              dateOnly: exit.dateOnly,
              quantity: qty
            }
          }
        });
      }
    });
  });

  return pairs;
};

// Get week number in year
const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

// Get all dates in a month
const getDatesInMonth = (year, month) => {
  const dates = [];
  const date = new Date(year, month, 1);
  while (date.getMonth() === month) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return dates;
};

// Format date to YYYY-MM-DD using local timezone (avoids UTC shift)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Calendar Heatmap Chart
 * Shows P&L by date in a calendar grid format
 * Color intensity represents P&L amount
 */
const CalendarHeatmap = ({ records, pairs = [] }) => {
  const [hoveredDate, setHoveredDate] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(50); // 0-100, default 50 (medium)
  const [expandedMonth, setExpandedMonth] = useState(null); // { year, month, label }
  const [selectedDate, setSelectedDate] = useState(null); // Selected date to show trade details

  // Calculate P&L by date and store individual trades
  const { dailyPL, dailyTrades, dateRange, maxAbsPL } = useMemo(() => {
    const pairsToUse = pairs && pairs.length > 0 ? pairs : calculatePairsFromRecords(records);

    if (pairsToUse.length === 0) {
      return { dailyPL: {}, dailyTrades: {}, dateRange: { start: null, end: null }, maxAbsPL: 0 };
    }

    const byDate = {};
    const tradesByDate = {};
    let minDate = null;
    let maxDate = null;

    pairsToUse.forEach(pair => {
      // Use exit date as trade completion date
      let date = pair.exit?.uiRecord?.dateOnly;
      if (!date && pair.exit?.uiRecord?.timestamp) {
        date = formatDateLocal(new Date(pair.exit.uiRecord.timestamp));
      }
      if (!date) return;

      if (!byDate[date]) {
        byDate[date] = { profit: 0, trades: 0, wins: 0, losses: 0 };
        tradesByDate[date] = [];
      }
      byDate[date].profit += pair.profit || 0;
      byDate[date].trades += 1;
      if ((pair.profit || 0) > 0) {
        byDate[date].wins += 1;
      } else {
        byDate[date].losses += 1;
      }

      // Store the pair for detailed view
      tradesByDate[date].push(pair);

      // Track date range
      const d = new Date(date);
      if (!minDate || d < minDate) minDate = d;
      if (!maxDate || d > maxDate) maxDate = d;
    });

    // Calculate max absolute P&L for color scaling
    const maxAbsPL = Math.max(
      ...Object.values(byDate).map(d => Math.abs(d.profit)),
      1 // Prevent division by zero
    );

    return {
      dailyPL: byDate,
      dailyTrades: tradesByDate,
      dateRange: { start: minDate, end: maxDate },
      maxAbsPL
    };
  }, [records, pairs]);

  // Get color for a date based on P&L
  const getColor = (profit) => {
    if (profit === 0) return '#e5e7eb'; // Gray for zero
    const intensity = Math.min(Math.abs(profit) / maxAbsPL, 1);
    const alpha = 0.3 + intensity * 0.7; // 0.3 to 1.0

    if (profit > 0) {
      // Green shades
      return `rgba(34, 197, 94, ${alpha})`;
    } else {
      // Red shades
      return `rgba(239, 68, 68, ${alpha})`;
    }
  };

  // Handle mouse events for tooltip
  const handleMouseEnter = (date, data, e) => {
    setHoveredDate({ date, data });
    setTooltipPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredDate(null);
  };

  if (!dateRange.start || !dateRange.end) {
    return (
      <div style={{
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No completed trades to display. Calendar requires matched Entry/Exit pairs.
      </div>
    );
  }

  // Generate calendar grid for the date range
  const startMonth = new Date(dateRange.start.getFullYear(), dateRange.start.getMonth(), 1);
  const endMonth = new Date(dateRange.end.getFullYear(), dateRange.end.getMonth() + 1, 0);

  // Get all months to display
  const months = [];
  let current = new Date(startMonth);
  while (current <= endMonth) {
    months.push({
      year: current.getFullYear(),
      month: current.getMonth(),
      label: current.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    });
    current.setMonth(current.getMonth() + 1);
  }

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Dynamic sizing based on zoom level (0-100)
  // cellSize ranges from 20px (zoom=0) to 48px (zoom=100)
  const cellSize = Math.round(20 + (zoomLevel / 100) * 28);
  const cellGap = Math.round(2 + (zoomLevel / 100) * 2);
  const headerHeight = Math.round(18 + (zoomLevel / 100) * 10);
  const fontSize = Math.round(8 + (zoomLevel / 100) * 6);

  // Calculate totals
  const totals = Object.values(dailyPL).reduce(
    (acc, d) => ({
      profit: acc.profit + (d.profit > 0 ? d.profit : 0),
      loss: acc.loss + (d.profit < 0 ? Math.abs(d.profit) : 0),
      trades: acc.trades + d.trades,
      tradingDays: acc.tradingDays + 1,
      winDays: acc.winDays + (d.profit > 0 ? 1 : 0)
    }),
    { profit: 0, loss: 0, trades: 0, tradingDays: 0, winDays: 0 }
  );

  return (
    <div style={{ width: '100%' }}>
      {/* Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        marginBottom: '16px',
        padding: '12px',
        background: '#f8f9fa',
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Trading Days</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{totals.tradingDays}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Win Days</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#22c55e' }}>
            {totals.winDays} ({totals.tradingDays > 0 ? ((totals.winDays / totals.tradingDays) * 100).toFixed(0) : 0}%)
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Profit</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#22c55e' }}>+${totals.profit.toFixed(0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Total Loss</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444' }}>-${totals.loss.toFixed(0)}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>Net P&L</div>
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: totals.profit - totals.loss >= 0 ? '#22c55e' : '#ef4444'
          }}>
            {totals.profit - totals.loss >= 0 ? '+' : ''}${(totals.profit - totals.loss).toFixed(0)}
          </div>
        </div>
      </div>

      {/* Zoom Slider */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        marginBottom: '16px',
        padding: '8px',
        background: '#f8f9fa',
        borderRadius: '8px'
      }}>
        <span style={{ fontSize: '13px', color: '#666' }}>Size:</span>
        <span style={{ fontSize: '12px', color: '#999' }}>S</span>
        <input
          type="range"
          min="0"
          max="100"
          value={zoomLevel}
          onChange={(e) => setZoomLevel(Number(e.target.value))}
          style={{
            width: '150px',
            cursor: 'pointer',
            accentColor: '#6366f1'
          }}
        />
        <span style={{ fontSize: '12px', color: '#999' }}>L</span>
        <span style={{
          fontSize: '12px',
          color: '#666',
          minWidth: '45px',
          textAlign: 'center'
        }}>
          {cellSize}px
        </span>
      </div>

      {/* Calendar Grid */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px',
        justifyContent: 'flex-start',
        padding: '8px'
      }}>
        {months.map((monthData, monthIdx) => {
          const dates = getDatesInMonth(monthData.year, monthData.month);
          const firstDayOfWeek = dates[0].getDay();
          const weeksInMonth = Math.ceil((dates.length + firstDayOfWeek) / 7);

          return (
            <div
              key={`${monthData.year}-${monthData.month}`}
              onClick={() => setExpandedMonth(monthData)}
              style={{
                flexShrink: 0,
                padding: '8px',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              title="Click to expand"
            >
              <div
                style={{
                  fontWeight: 'bold',
                  marginBottom: '8px',
                  fontSize: `${Math.round(12 + (zoomLevel / 100) * 4)}px`,
                  color: '#333'
                }}
              >
                {monthData.label}
              </div>

              <svg
                width={(cellSize + cellGap) * 7}
                height={headerHeight + (cellSize + cellGap) * weeksInMonth}
              >
                {/* Day headers */}
                {dayNames.map((day, i) => (
                  <text
                    key={day}
                    x={i * (cellSize + cellGap) + cellSize / 2}
                    y={headerHeight - 6}
                    textAnchor="middle"
                    fontSize={fontSize}
                    fill="#666"
                  >
                    {day.charAt(0)}
                  </text>
                ))}

                {/* Date cells */}
                {dates.map((date, i) => {
                  const dateStr = formatDateLocal(date);
                  const dayOfWeek = date.getDay();
                  const weekOfMonth = Math.floor((i + firstDayOfWeek) / 7);
                  const data = dailyPL[dateStr];
                  const hasData = data && data.trades > 0;

                  return (
                    <g key={dateStr}>
                      <rect
                        x={dayOfWeek * (cellSize + cellGap)}
                        y={headerHeight + weekOfMonth * (cellSize + cellGap)}
                        width={cellSize}
                        height={cellSize}
                        rx={4}
                        fill={hasData ? getColor(data.profit) : '#f3f4f6'}
                        stroke={hoveredDate?.date === dateStr ? '#333' : 'transparent'}
                        strokeWidth={2}
                        style={{ cursor: hasData ? 'pointer' : 'default' }}
                        onMouseEnter={(e) => hasData && handleMouseEnter(dateStr, data, e)}
                        onMouseLeave={handleMouseLeave}
                      />
                      <text
                        x={dayOfWeek * (cellSize + cellGap) + cellSize / 2}
                        y={headerHeight + weekOfMonth * (cellSize + cellGap) + cellSize / 2 + fontSize / 3}
                        textAnchor="middle"
                        fontSize={fontSize}
                        fill={hasData ? '#fff' : '#999'}
                        style={{ pointerEvents: 'none' }}
                      >
                        {date.getDate()}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          );
        })}
      </div>

      {/* Tooltip */}
      {hoveredDate && (
        <div style={{
          position: 'fixed',
          left: tooltipPos.x + 10,
          top: tooltipPos.y - 10,
          background: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          minWidth: '150px',
          pointerEvents: 'none'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {new Date(hoveredDate.date).toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            })}
          </div>
          <div style={{
            color: hoveredDate.data.profit >= 0 ? '#22c55e' : '#ef4444',
            fontWeight: 'bold',
            fontSize: '18px',
            marginBottom: '8px'
          }}>
            {hoveredDate.data.profit >= 0 ? '+' : ''}${hoveredDate.data.profit.toFixed(2)}
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            Trades: {hoveredDate.data.trades} ({hoveredDate.data.wins}W / {hoveredDate.data.losses}L)
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            Win Rate: {hoveredDate.data.trades > 0 ? ((hoveredDate.data.wins / hoveredDate.data.trades) * 100).toFixed(0) : 0}%
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '12px',
        fontSize: '13px',
        color: '#666'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '16px',
            height: '16px',
            borderRadius: '3px',
            background: 'rgba(34, 197, 94, 0.8)'
          }}></span>
          Profit
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '16px',
            height: '16px',
            borderRadius: '3px',
            background: 'rgba(239, 68, 68, 0.8)'
          }}></span>
          Loss
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '16px',
            height: '16px',
            borderRadius: '3px',
            background: '#f3f4f6'
          }}></span>
          No trades
        </span>
        <span>Darker = Higher P&L</span>
      </div>

      {/* Expanded Month Modal */}
      {expandedMonth && (() => {
        const dates = getDatesInMonth(expandedMonth.year, expandedMonth.month);
        const firstDayOfWeek = dates[0].getDay();
        const weeksInMonth = Math.ceil((dates.length + firstDayOfWeek) / 7);
        const expandedCellSize = 64;
        const expandedCellGap = 5;
        const expandedHeaderHeight = 36;
        const expandedFontSize = 18;

        // Get daily data for this month
        const monthDays = dates.map(date => {
          const dateStr = formatDateLocal(date);
          return {
            date,
            dateStr,
            data: dailyPL[dateStr] || null
          };
        }).filter(d => d.data && d.data.trades > 0);

        // Calculate month totals
        const monthTotals = monthDays.reduce(
          (acc, d) => ({
            profit: acc.profit + (d.data.profit > 0 ? d.data.profit : 0),
            loss: acc.loss + (d.data.profit < 0 ? Math.abs(d.data.profit) : 0),
            trades: acc.trades + d.data.trades,
            wins: acc.wins + d.data.wins,
            tradingDays: acc.tradingDays + 1,
            winDays: acc.winDays + (d.data.profit > 0 ? 1 : 0)
          }),
          { profit: 0, loss: 0, trades: 0, wins: 0, tradingDays: 0, winDays: 0 }
        );

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={() => { setExpandedMonth(null); setSelectedDate(null); }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '12px',
                maxWidth: '90vw',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header - Gradient */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}>
                <h2 style={{ margin: 0, fontSize: '22px', color: 'white', fontWeight: 600 }}>
                  {expandedMonth.label}
                </h2>
                <button
                  onClick={() => { setExpandedMonth(null); setSelectedDate(null); }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '24px', overflow: 'auto', flex: 1 }}>
                {/* Month Summary */}
                <div style={{
                  display: 'flex',
                  gap: '28px',
                  marginBottom: '24px',
                  padding: '16px 20px',
                  background: '#f8f9fa',
                  borderRadius: '10px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Trading Days</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{monthTotals.tradingDays}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Win Days</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>
                      {monthTotals.winDays} ({monthTotals.tradingDays > 0 ? ((monthTotals.winDays / monthTotals.tradingDays) * 100).toFixed(0) : 0}%)
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Profit</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>+${monthTotals.profit.toFixed(0)}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Loss</div>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>-${monthTotals.loss.toFixed(0)}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>Net P&L</div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 'bold',
                      color: monthTotals.profit - monthTotals.loss >= 0 ? '#22c55e' : '#ef4444'
                    }}>
                      {monthTotals.profit - monthTotals.loss >= 0 ? '+' : ''}${(monthTotals.profit - monthTotals.loss).toFixed(0)}
                    </div>
                  </div>
                </div>

              {/* Expanded Calendar with Side Panel */}
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                {/* Calendar Grid */}
                <div style={{ flexShrink: 0 }}>
                  <svg
                    width={(expandedCellSize + expandedCellGap) * 7}
                    height={expandedHeaderHeight + (expandedCellSize + expandedCellGap) * weeksInMonth}
                  >
                    {/* Day headers */}
                    {dayNames.map((day, i) => (
                      <text
                        key={day}
                        x={i * (expandedCellSize + expandedCellGap) + expandedCellSize / 2}
                        y={expandedHeaderHeight - 12}
                        textAnchor="middle"
                        fontSize={16}
                        fontWeight="bold"
                        fill="#666"
                      >
                        {day}
                      </text>
                    ))}

                    {/* Date cells */}
                    {dates.map((date, i) => {
                      const dateStr = formatDateLocal(date);
                      const dayOfWeek = date.getDay();
                      const weekOfMonth = Math.floor((i + firstDayOfWeek) / 7);
                      const data = dailyPL[dateStr];
                      const hasData = data && data.trades > 0;

                      const isSelected = selectedDate === dateStr;
                      const isHovered = hoveredDate?.date === dateStr;

                      return (
                        <g
                          key={dateStr}
                          style={{ cursor: hasData ? 'pointer' : 'default' }}
                          onMouseEnter={(e) => hasData && handleMouseEnter(dateStr, data, e)}
                          onMouseLeave={handleMouseLeave}
                          onClick={() => hasData && setSelectedDate(isSelected ? null : dateStr)}
                        >
                          <rect
                            x={dayOfWeek * (expandedCellSize + expandedCellGap)}
                            y={expandedHeaderHeight + weekOfMonth * (expandedCellSize + expandedCellGap)}
                            width={expandedCellSize}
                            height={expandedCellSize}
                            rx={6}
                            fill={hasData ? getColor(data.profit) : '#f3f4f6'}
                            stroke={isSelected ? '#6366f1' : (isHovered ? '#333' : (hasData ? '#fff' : 'transparent'))}
                            strokeWidth={isSelected ? 4 : (isHovered ? 3 : 2)}
                          />
                          <text
                            x={dayOfWeek * (expandedCellSize + expandedCellGap) + expandedCellSize / 2}
                            y={expandedHeaderHeight + weekOfMonth * (expandedCellSize + expandedCellGap) + 24}
                            textAnchor="middle"
                            fontSize={expandedFontSize}
                            fontWeight="bold"
                            fill={hasData ? '#fff' : '#999'}
                            style={{ pointerEvents: 'none' }}
                          >
                            {date.getDate()}
                          </text>
                          {hasData && (
                            <text
                              x={dayOfWeek * (expandedCellSize + expandedCellGap) + expandedCellSize / 2}
                              y={expandedHeaderHeight + weekOfMonth * (expandedCellSize + expandedCellGap) + 46}
                              textAnchor="middle"
                              fontSize={14}
                              fontWeight="bold"
                              fill="#fff"
                              style={{ pointerEvents: 'none' }}
                            >
                              {data.profit >= 0 ? '+' : ''}${Math.abs(data.profit) >= 1000 ? (data.profit / 1000).toFixed(1) + 'k' : data.profit.toFixed(0)}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </svg>
                </div>

                {/* Right Side Panel - Trade Details (only show when date selected) */}
                {selectedDate && dailyTrades[selectedDate] && (
                  <div style={{
                    flex: 1,
                    minWidth: '280px',
                    maxWidth: '360px',
                    background: '#f8f9fa',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    minHeight: expandedHeaderHeight + (expandedCellSize + expandedCellGap) * weeksInMonth - 20
                  }}>
                    {/* Selected Date Header */}
                      <div style={{
                        padding: '16px',
                        background: dailyPL[selectedDate]?.profit >= 0 ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start'
                        }}>
                          <div>
                            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                                weekday: 'long'
                              })}
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedDate(null)}
                            style={{
                              background: 'rgba(255,255,255,0.2)',
                              border: 'none',
                              fontSize: '16px',
                              cursor: 'pointer',
                              color: 'white',
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>
                        <div style={{ marginTop: '12px', fontSize: '24px', fontWeight: 'bold' }}>
                          {dailyPL[selectedDate]?.profit >= 0 ? '+' : ''}${dailyPL[selectedDate]?.profit.toFixed(2)}
                        </div>
                        <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '4px' }}>
                          {dailyTrades[selectedDate].length} trade(s) • {dailyPL[selectedDate]?.wins}W / {dailyPL[selectedDate]?.losses}L
                        </div>
                      </div>

                      {/* Trades List */}
                      <div style={{ padding: '12px', maxHeight: '300px', overflow: 'auto' }}>
                        {dailyTrades[selectedDate].map((trade, idx) => {
                          const entryTime = trade.entry?.uiRecord?.timestamp
                            ? new Date(trade.entry.uiRecord.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: ET_TIMEZONE })
                            : null;
                          const exitTime = trade.exit?.uiRecord?.timestamp
                            ? new Date(trade.exit.uiRecord.timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: ET_TIMEZONE })
                            : null;
                          const entryPrice = trade.entry?.uiRecord?.actualPrice;
                          const exitPrice = trade.exit?.uiRecord?.actualPrice;
                          const quantity = trade.quantity || trade.entry?.uiRecord?.quantity || trade.exit?.uiRecord?.quantity;

                          return (
                            <div
                              key={idx}
                              style={{
                                padding: '12px',
                                marginBottom: '8px',
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                borderLeft: `4px solid ${trade.profit >= 0 ? '#22c55e' : '#ef4444'}`
                              }}
                            >
                              {/* Symbol & P&L */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '10px'
                              }}>
                                <div style={{
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  color: '#333'
                                }}>
                                  {trade.symbol}
                                </div>
                                <div style={{
                                  fontWeight: 'bold',
                                  fontSize: '16px',
                                  color: trade.profit >= 0 ? '#22c55e' : '#ef4444'
                                }}>
                                  {trade.profit >= 0 ? '+' : ''}${trade.profit?.toFixed(2) || '0.00'}
                                </div>
                              </div>

                              {/* Entry */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '13px',
                                color: '#666',
                                marginBottom: '4px'
                              }}>
                                <span style={{ color: '#22c55e', fontWeight: 500 }}>Buy</span>
                                <span>
                                  {entryPrice ? `$${entryPrice.toFixed(2)}` : '-'}
                                  {entryTime && <span style={{ color: '#999', marginLeft: '8px' }}>@ {entryTime} ET</span>}
                                </span>
                              </div>

                              {/* Exit */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: '13px',
                                color: '#666',
                                marginBottom: '4px'
                              }}>
                                <span style={{ color: '#ef4444', fontWeight: 500 }}>Sell</span>
                                <span>
                                  {exitPrice ? `$${exitPrice.toFixed(2)}` : '-'}
                                  {exitTime && <span style={{ color: '#999', marginLeft: '8px' }}>@ {exitTime} ET</span>}
                                </span>
                              </div>

                              {/* Quantity */}
                              {quantity && (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: '13px',
                                  color: '#666'
                                }}>
                                  <span>Qty</span>
                                  <span>{quantity} shares</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                  </div>
                )}
              </div>
              </div>

              {/* Tooltip for expanded calendar - uses shared hoveredDate state */}
              {hoveredDate && (
                <div style={{
                  position: 'fixed',
                  left: tooltipPos.x + 10,
                  top: tooltipPos.y - 10,
                  background: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  zIndex: 10002,
                  minWidth: '180px',
                  pointerEvents: 'none'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '16px' }}>
                    {new Date(hoveredDate.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  <div style={{
                    color: hoveredDate.data.profit >= 0 ? '#22c55e' : '#ef4444',
                    fontWeight: 'bold',
                    fontSize: '22px',
                    marginBottom: '10px'
                  }}>
                    {hoveredDate.data.profit >= 0 ? '+' : ''}${hoveredDate.data.profit.toFixed(2)}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                    Trades: {hoveredDate.data.trades} ({hoveredDate.data.wins}W / {hoveredDate.data.losses}L)
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Win Rate: {hoveredDate.data.trades > 0 ? ((hoveredDate.data.wins / hoveredDate.data.trades) * 100).toFixed(0) : 0}%
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CalendarHeatmap;
